<template>
  <header id="lm-header" :class="{ collapsed: !menuExpanded }">
    <div class="navbar-custom d-flex justify-content-between align-items-center position-relative pl-3 pr-2" style="z-index: 11">
      <section class="d-flex align-items-center">
        <button
          class="menu-toggle-button mr-4 bg-gray-0 rounded-full w-10 h-10 flex justify-center align-items-center"
          @click="$emit('toggleMenu'); menuExpanded = !menuExpanded"
        >
          <i class="mdi mdi-menu"></i>
        </button>
        <router-link to="/" id="lm-logo-fav">
          <img :src="$store.state.settings.faviconDot" width="30" alt="Logo">
        </router-link>
      </section>

      <div class="flex flex-col sm:flex-row sm:space-x-4">
        <LmTrialNotice v-if="isOnTrial" :daysLeft="daysLeft" targetCta="/ajustes?tab=1" />
        <LmAcceptNotifications />
      </div>

      <div class="d-flex">
        <!-- Notifications -->
        <b-dropdown
          no-caret
          variant="link"
          toggle-class="text-decoration-none notifications-toggle px-0"
          right text="Right align"
          menu-class="notifications-card notifications-box shadow bg-white py-0"
          @toggle="onToggleNotifications"
          ref="notifications"
        >
          <template #button-content>
            <i
              class="mdi mdi-bell-outline"
              :class="{'text-primary': showingNotifications, 'text-secondary': !showingNotifications, 'animate-bell-icon': unreadNotifications}"
              style="font-size: 1.6rem"
            ></i>
            <span class="noti-icon-badge" v-if="unreadNotifications"></span>
          </template>

          <b-card
            no-body
            footer-bg-variant="white"
            footer-class="border-top"
            header-bg-variant="primary"
            header-class="py-1"
            class="m-0"
          >
            <template #header class="bg-white">
              <h5 class="text-left text-white">{{ $t('notifications') }}</h5>
            </template>

            <div v-if="!notifications.length">
              <LmSvgIcon
                icon="LmNoNotifyIcon"
                wrapperClass="my-4 d-flex align-items-center flex-column"
                text="No tienes notificaciones"
              />
            </div>

            <b-list-group flush class="scroll" v-else>
              <b-list-group-item
                v-for="(notification, index) in notifications"
                :key="index"
                :to="notification.click_action"
                :title="notification.title"
              >
              <div class="d-flex w-100 justify-content-between">
                <h5 class="mb-1">{{notification.title}}</h5>
                <small>{{notificationDate(notification)}}</small>
              </div>
              <p class="mb-1 notification-body">
                {{notification.body}}
              </p>
              <a-button
                class="mt-2"
                size="small"
                @click.prevent="onSign(notification)"
                v-if="showQuickSignButton(notification)"
              >
                Firmar documento
              </a-button>
              <a-button
                class="mt-2"
                size="small"
                @click.prevent="onDownload(notification)"
                v-if="showDownloadButton(notification)"
              >
                Descargar
              </a-button>
              </b-list-group-item>
            </b-list-group>

            <template #footer>
              <h5 class="text-center">
                <router-link :to="allNotificationsRoute">{{ $t('show_more') }}</router-link>
              </h5>
            </template>
          </b-card>
        </b-dropdown>

        <!-- Profile -->
        <b-dropdown
          id="ddown-header"
          variant="transparent"
          no-caret
          class="p-0"
          menu-class="shadow"
        >
          <template slot="button-content" class="bg-gray-600">
            <div class="account-user-avatar flex justify-center items-center">
              <LmProfilePicture :size="'2.9rem'"/>
            </div>
            <div class="wrapperUserInfo hidden xl:flex justify-center flex-col h-ful mt-1">
              <span class="account-user-name">{{ accountUserName }}</span>
              <div v-for="(r, index) in roles" :key="index">
                <span class="account-position">{{ r }}</span>
              </div>
              <span class="account-position">{{ role }}</span>
              <!-- <span class="account-position" v-if="extra.customerName">{{ extra.customerName }}</span> -->
            </div>
          </template>
          <b-dropdown-header>
            <h6>{{ $t('hello_name', {firstname: user.firstname}) }}</h6>
          </b-dropdown-header>
          <template v-if="extra.customers && extra.customers.length > 1">
            <b-dropdown-divider class="divider"></b-dropdown-divider>
            <b-dropdown-item
              v-for="item in extra.customers"
              :key="item.id"
              :active="item.id === extra.customerId"
              @click="onChangeCustomer(item.id)"
            >
              {{ item.name }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
          </template>
          <b-dropdown-item :to="userProfileRoute">
            <i class="mdi mdi-account-circle"></i>
            <span>{{ $t('my_profile') }}</span>
          </b-dropdown-item>
          <b-dropdown-item :to="el.path" v-for="(el, index) in dropdownMenuItems.data.dropdownMenuItems" :key="index">
            <i class="mdi" :class="el.icon"></i>
            <span>{{el.name}}</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="!emailWasConfirmed" @click="triggerEmailConfirmation()">
            <i class="mdi mdi-format-list-checks"></i>
            <span>{{ $t('confirm_email') }}</span>
          </b-dropdown-item>

          <b-dropdown-item @click="logout()">
            <i class="mdi mdi-logout"></i>
            <span>{{ $t('Logout') }}</span>
          </b-dropdown-item>
          <template v-if="showTyC">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item v-b-modal.tyc-current-modal>
              <b-link class="mb-0 text-wrap" style="font-size: 0.75rem">
                Terminos y Condiciones
              </b-link>
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.privacy-policies-current-modal>
              <b-link class="mb-0 text-wrap" style="font-size: 0.75rem">
                Política de Privacidad
              </b-link>
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </div>

      <b-modal
        id="tyc-current-modal"
        ok-only
        :ok-title="$t('confirm')"
        scrollable
        hide-header
      >
        <small>Version: {{ appUsageTerms.version }}</small>
        <div v-html="appUsageTerms.termsAndConditions"></div>
      </b-modal>
      <b-modal
        id="privacy-policies-current-modal"
        ok-only
        :ok-title="$t('confirm')"
        scrollable
        hide-header
      >
        <small>Version: {{ appUsageTerms.version }}</small>
        <div v-html="appUsageTerms.privacyPolicies"></div>
      </b-modal>
      <lmConfirmationModal
        ref="lmConfirmationModalFirmarToken" title="Aviso"
        message="Va a iniciar el proceso de firma del documento.  ¿Está seguro?"
      />
      <LmSendOTP
        ref="lmValidationModalFirmarToken" title="Aviso"
        message="Un código fue enviado a su mail, por favor ingreselo a continuación." @ok="setToken"
      />
    </div>
  </header>
</template>

<script>
import lmConfirmationModal from '@/components/modals/LmConfirmationModal'
import LmProfilePicture from '@/components/LmProfilePicture'
import LmTrialNotice from '@/components/LmTrialNotice'
import LmAcceptNotifications from '@/components/LmAcceptNotifications'
import LmSendOTP from '@/components/modals/LmSendOTP'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import lmToasts from '@/mixins/lmToasts.js'
import commonsApi from '@/api/commons'
import axios from 'axios'
import moment from 'moment'
import 'moment/locale/es'
moment.locale('es')

export default {
  mixins: [lmToasts],
  data () {
    return {
      lumbreSignerPort: process.env?.VUE_APP_LUMBRESIGNER_PORT || '8081',
      otpToken: null,
      isShowedViewMore: true,
      showingNotifications: false,
      dropdownMenuItems: {
        data: {
          dropdownMenuItems: {}
        }
      },
      menuExpanded: true
    }
  },
  components: {
    LmProfilePicture,
    LmTrialNotice,
    LmAcceptNotifications,
    lmConfirmationModal,
    LmSendOTP
  },
  props: {
    activeShowMoreAt: {
      type: Number,
      default: 10
    }
  },
  computed: {
    showTyC () {
      return process.env?.VUE_APP_ENABLE_TYC === 'yes'
    },
    enableLumileAppSigner () {
      return process.env?.VUE_APP_ENABLE_LUMILE_APP_SIGNER === 'yes'
    },
    allNotificationsRoute () {
      if (process?.env?.VUE_APP_ENABLE_MICROSERVICES_NOTIFICATIONS === 'yes') {
        return `/${process.env.VUE_APP_LUMBRE_MICROSERVICES_NOTIFICATIONS_PATH}`
      } else {
        return `/${process.env.VUE_APP_LUMBRE_NOTIFICATIONS_PATH}`
      }
    },
    userProfileRoute () {
      if (process?.env?.VUE_APP_LUMBRE_PROFILEV2_ENABLED === 'yes') {
        return `/profile`
      } else {
        return `/user-profile`
      }
    },
    userInitials () {
      if (this.user.firstname && this.user.lastname) {
        return this.user.firstname.charAt(0) + this.user.lastname.charAt(0)
      } else {
        return ''
      }
    },
    ...mapState('session', ['profilePicture', 'role', 'roles', 'extra']),
    ...mapGetters('session', ['accountUserName', 'emailWasConfirmed', 'isOnTrial', 'daysLeft']),
    ...mapState('settings', ['appUsageTerms']),
    ...mapState('notifications', ['notifications', 'unreadNotifications'])
  },
  methods: {
    async onSign (notification) {
      try {
        // NOTA LR: en vista de que una firma ahora tiene validaciones
        // como el garabato o la geolocalizacion, es mejor y mas sencillo redireccionar
        // al documento y ejecutar el método de firma automaticamente
        // que repetir toda la lógica acá
        this.$router.push({
          path: notification.click_action,
          query: { immediateSign: true }
        })

        // const { _id, pushContext } = notification
        // await this.$refs.lmConfirmationModalFirmarToken.lmShow()
        // if (pushContext.signatureType === 'electronic') {
        //   await commonsApi.axiosPostWrapper('/documents/generateotp', {
        //     id: pushContext.documentId
        //   })
        //   this.$refs.lmValidationModalFirmarToken.setIsCommentProps(true, true, 'Codigo', 'token')
        //   await this.$refs.lmValidationModalFirmarToken.lmShow()
        //   await commonsApi.axiosPostWrapper(`/documents/sign`, {
        //     id: pushContext.documentId,
        //     token: this.otpToken
        //   })
        // } else if (pushContext.signatureType === 'electronic_without_otp') {
        //   await commonsApi.axiosPostWrapper(`/documents/sign`, {
        //     id: pushContext.documentId,
        //     token: this.otpToken
        //   })
        // } else if (pushContext.signatureType === 'digital') {
        //   if (this.enableLumileAppSigner) {
        //     try {
        //       await this.checkLumbreDocsSigner()
        //     } catch (error) {
        //       this.errorOcurredOnSign = true
        //     }
        //     const r = await commonsApi.axiosGetWrapper(`/documents/generatejnlpV2/${pushContext.documentId}`)
        //     await commonsApi.axiosPostWrapper('http://localhost:8081/initSR', {
        //       url: r.data.targetURL
        //     })
        //   } else {
        //     const tempLink = document.createElement('a')
        //     tempLink.href = `${process.env.VUE_APP_API_URL}documents/generatejnlp/${pushContext.documentId}`
        //     tempLink.setAttribute('download', `documento_afirmar.jnlp`)
        //     tempLink.click()
        //     this.lmToastSuccessMessage(`Para continuar debe finalizar el proceso de firma desde la aplicacion.`, 'Enviado al Firmador.')
        //   }
        // }

        // this.lmToastSuccessMessage(`Se envió a firmar ${pushContext.documentName || ' el documento'} correctamente.`, 'Documento enviado a firmar.')

        // // Marco la notificacion como terminada
        // await commonsApi.axiosPutWrapper('/notifications/mark-as-done', {
        //   id: _id
        // })
      } catch (error) {
        console.log(error)
      }
    },
    async triggerEmailConfirmation () {
      try {
        this.lmModalInformationMessage({
          resultSubtitle: '',
          modalClosable: false,
          buttons: [
            {
              btnText: this.$t('i_will_do_it_later'),
              btnType: 'danger',
              btnGhost: true,
              btnLink: 'close'
            },
            {
              btnText: this.$t('continue'),
              btnCallback: async () => {
                await commonsApi.axiosPostWrapper('/users/request-email-validation')
                this.lmToastSuccessMessage(`${this.$t('email_confirmation_request_done_successfully')}`)
              }
            }
          ],
          message: this.$t('previous_email_confirmation_message')
        })
      } catch (error) {
        console.log(error)
      }
    },
    onDownload (notification) {
      const a = document.createElement('a')
      a.download = ''
      a.href = notification.pushContext.link
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      a.remove()
    },
    setToken (data) {
      this.otpToken = data.comment
    },
    async checkLumbreDocsSigner () {
      try {
        const api = axios.create()
        const localSignerPromise = api(`http://localhost:${this.lumbreSignerPort}/version`)
        const signerLastVersionPromise = api('/versions/lumbre-signer')
        const [localSigner, signerLastVersion] = await Promise.all([localSignerPromise, signerLastVersionPromise])
        let error
        if (parseFloat(localSigner.data.version) < parseFloat(signerLastVersion.data)) {
          error = {
            type: 'update_required',
            message: 'LumbreDocs Signer necesita actualizarse.'
          }
        }
        if (error) throw error
      } catch (error) {
        if (error?.type === 'update_required') {
          this.lmToastDangerMessage(
            error.message,
            'Error'
          )
        } else {
          if (process.env?.VUE_APP_ENABLE_REAMAZE === 'yes') {
            window.Reamaze.popup('/kb/info-tecnica/como-descargar-el-firmador')
            this.lmToastDangerMessage(
              'Para poder hacer una firma digital necesita tener instalado LumbreDocs Signer, siga los pasos que se muestran en la ayuda.',
              'Error'
            )
          }
        }
        throw error
      }
    },
    showQuickSignButton (notification) {
      return notification.pushContext?.quickSign && !notification.done
    },
    showDownloadButton (notification) {
      return notification.pushContext?.type === 'download'
    },
    onToggleNotifications () {
      this.showingNotifications = !this.showingNotifications
      if (this.showingNotifications) {
        this.clearUnreadNotifications()
      }
    },
    notificationDate (notification) {
      if (process?.env?.VUE_APP_ENABLE_MICROSERVICES_NOTIFICATIONS === 'yes') {
        return notification.timeAgoDate
      } else {
        return notification.date
      }
    },
    hideDropDown () {
      this.$refs.notifications.hide(true)
    },
    clearUnreadNotifications () {
      this.actionClearUnreadNotifications()
    },
    async logout () {
      try {
        await this.$store.dispatch('session/logout')
        location.href = '/login'
      } catch (error) {
        console.log('error logout header', error)
      }
    },
    async onChangeCustomer (id) {
      // TODO. Cambio el cliente del usuario logueado
      try {
        await commonsApi.axiosPutWrapper('/users/change-customer', { id })
        this.mutateCustomerId(id)
        this.$router.go()
      } catch (error) {
        console.log(error)
      }
    },
    ...mapMutations('menu', ['mutationIsOpen']),
    ...mapMutations('notifications', ['mutateUpdateTimeAgo']),
    ...mapMutations('session', ['mutateCustomerId']),
    ...mapActions('notifications', ['actionClearUnreadNotifications']),
    ...mapActions('commons', ['getItems'])
  },
  async created () {
    const getUser = await commonsApi.axiosGetWrapper(`/profile/userinfo`)
    this.user = getUser.data
  },
  async mounted () {
    if (process?.env?.VUE_APP_ENABLE_MICROSERVICES_NOTIFICATIONS === 'yes') {
      let updatingTimeAgo
      this.$refs.notifications.$on('show', () => {
        this.mutateUpdateTimeAgo()
        this.showingNotifications = true
        updatingTimeAgo = setInterval(() => {
          this.mutateUpdateTimeAgo()
        }, 1000 * 60)
      })
      this.$refs.notifications.$on('hidden', () => {
        clearInterval(updatingTimeAgo)
        this.showingNotifications = false
      })
    }
    if (process.env.VUE_APP_DROPDOWN_MIDDLE_MENU === 'yes') {
      this.dropdownMenuItems = await commonsApi.getItems({
        path: `${process.env.VUE_APP_API_SETTINGS_URI}/dropdownmenu/`
      })
    }
  },
  watch: {
    '$route' () {
      this.hideDropDown()
    }
  }
}
</script>

<style lang="sass">
  #lm-header
    position: fixed;
    z-index: 500;
    right: 0;
    width: calc( 100% - 250px )
    transition: width ease-in-out .25s;
    background: #fff
    border-bottom: 1px solid rgba(152,166,173,.3)
    padding: 0
    @media(max-width: $lg)
      width: 100%
    @keyframes shake
      10%
        transform: rotate(15deg)
      20%
        transform: rotate(-15deg)
      30%
        transform: rotate(15deg)
      50%
        transform: rotate(0deg)

    .logo-img
      width: 100%
      height: auto
      max-width: 200px
      max-height: 50px
    #lm-logo-fav
      display: none
      @media(max-width: $lg)
        display: initial
    .menu-toggle-button
      // background: transparent
      // border: none
      font-size: 1.5rem
      @media(max-width: $lg)
        display: none
    .mdi-bell-outline.animate-bell-icon:before
      animation: shake 1.5s forwards infinite
    .scroll
      overflow-y: auto
      &::-webkit-scrollbar-track
        background: #fff
      &::-webkit-scrollbar
        width: 8px
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: #888
      &::-webkit-scrollbar-thumb:hover
        background: #555
    .notification-body
      white-space: nowrap
      overflow: hidden
      text-overflow: ellipsis
    .notifications-box
      width: 350px
      max-height: 500px
      .card
        max-height: 500px
    .notifications-toggle
      &:focus
        outline: none !important
        box-shadow: none !important
    .notifications-card
      width: 360px
      top: -15px !important
    .navbar-custom
      padding: 0 0px
      background-color: #fff
      // box-shadow: 0 0 350px 0 rgba(154,161,171,.15)
      min-height: 64px
      @media(max-width: $xl)
        padding: 0 10px
      .button-menu-mobile
        border: none
        color: #313a46
        height: 70px
        line-height: 64px
        width: 60px
        background-color: transparent
        font-size: 24px
        cursor: pointer
        float: left
        &:focus
          outline: none
      #notification-header
        float: right
        position: static
        @media(min-width: $xl)
          position: relative
        .media-body
          max-width: 85%
        .notification-header-body
          font-size: 14px
        .notification-header-date
          font-size: 11px
        .notify-all
          font-size: 14px
          color: $primaryThemeColor
          padding: 10px 20px
          &:hover
            text-decoration: none
            background-color: #f8f9fa
            color: #4250f2
        .noti-icon-badge
          display: inline-block
          position: absolute
          top: 20px
          right: 15px
          border-radius: 50%
          height: 10px
          width: 10px
          background-color: #fa5c7c
        .noti-icon
          font-size: 22px
          vertical-align: middle
          line-height: 70px
        .dropdown-menu
          width: 100%
          @media(min-width: $md)
            width: 320px
        .noti-title
          background-color: #fff
          padding: 15px 20px
          h5
            font-size: 15px
          small
            font-size: 12px
        .wrapper-dropdown-item
          max-height: 230px
          overflow: auto
          .notify-item
            padding: 10px 20px
            &:hover
              background: red
            .notify-icon
              float: left
              height: 36px
              width: 36px
              line-height: 36px
              text-align: center
              margin-right: 10px
              border-radius: 50%
              color: #fff
              i
                margin: 0
            .notify-details
              margin-bottom: 0
              overflow: hidden
              margin-left: 45px
              text-overflow: ellipsis
              white-space: nowrap
              font-size: 14px
              b
                font-weight: 500
              small
                display: block
                font-size: 12px
      #ddown-header
        color: $mutedFontThemeColor
        min-width: 32px
        display: block
        margin: 0 8px
        text-align: left !important
        position: relative
        background-color: #fafbfd
        border-left: 0px solid #eee
        min-height: 64px
        float: right
        padding: 0px 0px !important
        .account-user-avatar
          float: left
          img
            width: 32px
            height: 32px
            max-width: 100%
            max-height: 100%
        .wrapperUserInfo
          text-align: left
          padding-left: 10px
          float: left
          .account-user-name
            font-weight: 600
            color: #212529
            font-size: 16px
            display: block
            white-space: initial
          .account-position
            display: block
            font-size: 12px
            margin-top: -3px
            // color: $mutedFontThemeColor
            color: #6c757d
            white-space: initial
      .dropdown-menu
        z-index: $zIndexLmDropdownMenu
        .dropdown-item
          padding: 7px 20px
          display: block
          width: 100%
          clear: both
          font-weight: 400
          text-align: inherit
          white-space: nowrap
          background-color: transparent
          border: 0
          &.active
            background-color: #007bff
            &:hover
              background-color: #007bff
          &:hover
            background-color: #f8f9fa
          i
            vertical-align: middle
            margin-right: 5px
            font-size: 14px
          span
            font-size: 14px
            vertical-align: middle
      .dropdown-header
        display: block
        padding: .25rem 1.5rem
        margin-bottom: 0
        font-size: .875rem
        color: inherit
        white-space: nowrap
        &.noti-title
          padding: 15px 20px
          h6
            font-size: 12px
      .app-search
        padding: 16px 0
        overflow: hidden
        float: left
        button
          font-size: 14px
        form
          max-width: 320px
          .input-group
            position: relative
            display: flex
            flex-wrap: wrap
            align-items: stretch
            width: 100%
        .app-search__main-input
          border: none
          height: 38px
          padding-left: 40px
          padding-right: 20px
          background-color: #f1f3fa
          box-shadow: none
          &::-webkit-input-placeholder
            color: #c1c1c1
            font-size: 14px
        .mdi-magnify
          position: absolute
          z-index: 9
          font-size: 20px
          line-height: 38px
          left: 10px
        .input-group-append
          margin-left: 0
          z-index: 4
  #lm-header.collapsed
    width: calc( 100% - 82px )

  // :style="{ width: menuExpanded ? `calc( 100% - 250px )` : `calc( 100% - 82px )` }"
</style>
